import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowUpRight,
  Image as ImageIcon,
  Link as LinkIcon,
  Maximize2,
  Play,
  X,
} from 'lucide-react';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { TrainingContentDisplayItem } from '../../../hooks/useSessionDetail';

interface NeedHelpSectionProps {
  items: TrainingContentDisplayItem[];
}

const titleFor = (item: TrainingContentDisplayItem) => {
  if (item.title && item.title.trim()) return item.title.trim();
  if (item.kind === 'video') return i18n.t('feedback.watchExample');
  if (item.kind === 'link') return i18n.t('feedback.openResource');
  return i18n.t('feedback.viewImage');
};

/** Leading glyph describes what the resource *is*. */
const TypeIcon: React.FC<{ kind: TrainingContentDisplayItem['kind'] }> = ({ kind }) => {
  if (kind === 'video') return <Play size={14} fill="currentColor" />;
  if (kind === 'link') return <LinkIcon size={14} />;
  return <ImageIcon size={14} />;
};

const CLOSE_KEYS = new Set(['Escape', 'Esc']);
const FOCUSABLE = 'button, a[href], video, [tabindex]:not([tabindex="-1"])';

/**
 * "Need Help?" — optional training content a Super Admin attached to this step.
 * Nothing auto-plays: media opens in a modal only on click; links open in a new tab.
 */
const NeedHelpSection: React.FC<NeedHelpSectionProps> = ({ items }) => {
  const { t } = useTranslation();
  const [active, setActive] = useState<TrainingContentDisplayItem | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  // Element that opened the modal, so focus can be handed back on close.
  const openerRef = useRef<HTMLElement | null>(null);

  // Only surface items we can actually open.
  const usable = items.filter(i => !!i.url);

  const close = useCallback(() => setActive(null), []);

  const open = (item: TrainingContentDisplayItem, event: React.MouseEvent<HTMLButtonElement>) => {
    if (item.kind === 'link') {
      window.open(item.url as string, '_blank', 'noopener,noreferrer');
      return;
    }
    openerRef.current = event.currentTarget;
    setActive(item);
  };

  // Move focus into the modal on open and restore it to the opener on close.
  useEffect(() => {
    if (!active) return;
    const opener = openerRef.current;
    closeRef.current?.focus();
    return () => opener?.focus();
  }, [active]);

  // Escape to dismiss, and keep Tab cycling inside the dialog.
  useEffect(() => {
    if (!active) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (CLOSE_KEYS.has(e.key)) {
        e.stopPropagation();
        close();
        return;
      }
      if (e.key !== 'Tab') return;

      const modal = modalRef.current;
      if (!modal) return;
      const focusable = Array.from(modal.querySelectorAll<HTMLElement>(FOCUSABLE))
        .filter(el => !el.hasAttribute('disabled'));
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const current = document.activeElement as HTMLElement | null;

      // Wrap at both ends, and pull focus back if it has escaped the dialog entirely.
      if (!current || !modal.contains(current)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && current === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && current === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [active, close]);

  if (usable.length === 0) return null;

  return (
    <div className="need-help">
      <span className="need-help__label">{t('feedback.learnMore')}</span>

      <ul className="need-help__items">
        {/* Index is part of the key because the same resource can legitimately be
            attached twice to one step, which would otherwise collide. */}
        {usable.map((item, i) => (
          <li key={`${item.kind}-${item.url}-${i}`} className="need-help__row">
            <button type="button" className="need-help__item" onClick={e => open(item, e)}>
              <span className="need-help__icon" aria-hidden="true">
                <TypeIcon kind={item.kind} />
              </span>
              <span className="need-help__name">{titleFor(item)}</span>
              <span className="need-help__action" aria-hidden="true">
                {item.kind === 'link' ? <ArrowUpRight size={14} /> : <Maximize2 size={13} />}
              </span>
            </button>
          </li>
        ))}
      </ul>

      {active && (
        <div className="need-help__overlay" onClick={close}>
          <div
            ref={modalRef}
            className="need-help__modal"
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={active.title || t('common.trainingContent')}
          >
            <button
              ref={closeRef}
              type="button"
              className="need-help__close"
              onClick={close}
              aria-label={t('common.close')}
            >
              <X size={18} />
            </button>
            {active.kind === 'video' ? (
              // playsInline keeps iOS Safari from force-expanding to native fullscreen,
              // which would tear the clip out of the dialog and bypass its focus trap.
              <video
                className="need-help__media"
                src={active.url as string}
                controls
                playsInline
                preload="metadata"
              />
            ) : (
              <img className="need-help__media" src={active.url as string} alt={active.title || t('feedback.trainingContentAlt')} />
            )}
            {(active.title || active.description) && (
              <div className="need-help__meta">
                {active.title && <div className="need-help__meta-title">{active.title}</div>}
                {active.description && <div className="need-help__meta-desc">{active.description}</div>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NeedHelpSection;
